import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Users, MapPin, Calendar, Briefcase, Heart, Eye, Share2, 
  ArrowLeft, Play, Music, Image as ImageIcon, UserPlus, UserMinus 
} from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UserProfilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { token, user: currentUser } = useAuth();
  const { toast } = useToast();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [friendshipStatus, setFriendshipStatus] = useState('not_friends');
  const [friendRequestId, setFriendRequestId] = useState(null);

  useEffect(() => {
    fetchProfile();
    if (token && userId) {
      checkFollowStatus();
      checkFriendshipStatus();
    }
  }, [userId, token]); // Dependencies are correct

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/networking/profiles/${userId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setProfile(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkFollowStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/social/is-following/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsFollowing(response.data.is_following);
    } catch (error) {
      console.error('Error checking follow status:', error);
    }
  };

  const checkFriendshipStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/social/friendship-status/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFriendshipStatus(response.data.status);
      if (response.data.request_id) {
        setFriendRequestId(response.data.request_id);
      }
    } catch (error) {
      console.error('Error checking friendship status:', error);
    }
  };

  const handleFriendRequest = async () => {
    if (!token) {
      toast({
        variant: 'destructive',
        title: 'Login Required',
        description: 'Please login to send friend requests',
      });
      navigate('/login');
      return;
    }

    setFollowLoading(true);
    try {
      if (friendshipStatus === 'friends') {
        // Remove friend
        await axios.delete(`${API_URL}/api/social/friends/remove/${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFriendshipStatus('not_friends');
        toast({
          title: 'Friend Removed',
          description: `You removed ${profile.user.username} from friends`,
        });
      } else if (friendshipStatus === 'request_received') {
        // Accept friend request
        await axios.post(`${API_URL}/api/social/friend-request/accept/${friendRequestId}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFriendshipStatus('friends');
        toast({
          title: 'Friend Request Accepted',
          description: `You are now friends with ${profile.user.username}`,
        });
      } else if (friendshipStatus === 'not_friends') {
        // Send friend request
        await axios.post(`${API_URL}/api/social/friend-request/send/${userId}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFriendshipStatus('request_sent');
        toast({
          title: 'Friend Request Sent',
          description: `Friend request sent to ${profile.user.username}`,
        });
      }
      checkFriendshipStatus();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update friendship',
      });
    } finally {
      setFollowLoading(false);
    }
  };

  const handleFollow = async () => {
    if (!token) {
      toast({
        variant: 'destructive',
        title: 'Login Required',
        description: 'Please login to follow users',
      });
      navigate('/login');
      return;
    }

    setFollowLoading(true);
    try {
      if (isFollowing) {
        await axios.delete(`${API_URL}/api/social/unfollow/${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsFollowing(false);
        toast({
          title: 'Unfollowed',
          description: `You unfollowed ${profile.user.username}`,
        });
      } else {
        await axios.post(`${API_URL}/api/social/follow/${userId}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsFollowing(true);
        toast({
          title: 'Following',
          description: `You are now following ${profile.user.username}`,
        });
      }
      // Refresh profile to update follower count
      fetchProfile();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update follow status',
      });
    } finally {
      setFollowLoading(false);
    }
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      admin: { color: 'bg-red-500', label: 'Admin' },
      artist: { color: 'bg-purple-500', label: 'Artist' },
      moderator: { color: 'bg-blue-500', label: 'Moderator' },
      user: { color: 'bg-gray-500', label: 'User' }
    };
    return roleConfig[role] || roleConfig.user;
  };

  const getContentIcon = (type) => {
    switch (type) {
      case 'video': return <Play className="w-4 h-4" />;
      case 'audio': return <Music className="w-4 h-4" />;
      case 'image': return <ImageIcon className="w-4 h-4" />;
      default: return <Briefcase className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profile || !profile.user) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black flex items-center justify-center">
        <div className="text-center">
          <Users className="w-16 h-16 mx-auto mb-4 text-gray-600" />
          <p className="text-gray-400 mb-4">Profile not found</p>
          <Button onClick={() => navigate('/networking')}>
            Back to Networking
          </Button>
        </div>
      </div>
    );
  }

  const { user, content, stats } = profile;
  const roleBadge = getRoleBadge(user.role);

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black">
      {/* Back Button */}
      <div className="container mx-auto px-6 py-4">
        <Button
          onClick={() => navigate('/networking')}
          variant="ghost"
          className="text-gray-400 hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Networking
        </Button>
      </div>

      {/* Profile Header */}
      <div className="bg-gradient-to-r from-yellow-400/20 to-purple-500/20 border-b border-gray-800">
        <div className="container mx-auto px-6 py-12">
          <div className="flex flex-col md:flex-row items-start gap-8">
            {/* Avatar */}
            <div className="w-32 h-32 rounded-full bg-gradient-to-r from-yellow-400 to-purple-500 flex items-center justify-center flex-shrink-0 p-1">
              <div className="w-full h-full rounded-full bg-gray-900 flex items-center justify-center">
                {user.profile_picture ? (
                  <img 
                    src={user.profile_picture} 
                    alt={user.username}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  <Users className="w-16 h-16 text-white" />
                )}
              </div>
            </div>

            {/* Profile Info */}
            <div className="flex-1">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h1 className="text-4xl font-black text-white mb-2">
                    {user.display_name || user.username}
                  </h1>
                  <p className="text-xl text-gray-400">@{user.username}</p>
                </div>
                <div className="flex items-center gap-3">
                  <Badge className={`${roleBadge.color} text-white`}>
                    {roleBadge.label}
                  </Badge>
                  {token && currentUser?.id !== userId && (
                    <>
                      <Button
                        onClick={handleFriendRequest}
                        disabled={followLoading}
                        className={
                          friendshipStatus === 'friends'
                            ? "bg-blue-600 hover:bg-blue-700 text-white"
                            : friendshipStatus === 'request_sent'
                            ? "bg-gray-700 hover:bg-gray-600 text-white"
                            : friendshipStatus === 'request_received'
                            ? "bg-green-600 hover:bg-green-700 text-white"
                            : "bg-gradient-to-r from-blue-400 to-purple-500 hover:from-blue-500 hover:to-purple-600 text-white font-bold"
                        }
                      >
                        {followLoading ? (
                          'Loading...'
                        ) : friendshipStatus === 'friends' ? (
                          <>
                            <Users className="w-4 h-4 mr-2" />
                            Friends
                          </>
                        ) : friendshipStatus === 'request_sent' ? (
                          <>
                            <Users className="w-4 h-4 mr-2" />
                            Request Sent
                          </>
                        ) : friendshipStatus === 'request_received' ? (
                          <>
                            <Users className="w-4 h-4 mr-2" />
                            Accept Request
                          </>
                        ) : (
                          <>
                            <UserPlus className="w-4 h-4 mr-2" />
                            Add Friend
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={handleFollow}
                        disabled={followLoading}
                        className={isFollowing 
                          ? "bg-gray-700 hover:bg-gray-600 text-white" 
                          : "bg-gradient-to-r from-yellow-400 to-purple-500 hover:from-yellow-500 hover:to-purple-600 text-black font-bold"
                        }
                      >
                        {followLoading ? (
                          'Loading...'
                        ) : isFollowing ? (
                          <>
                            <UserMinus className="w-4 h-4 mr-2" />
                            Unfollow
                          </>
                        ) : (
                          <>
                            <UserPlus className="w-4 h-4 mr-2" />
                            Follow
                          </>
                        )}
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Bio */}
              {user.bio && (
                <p className="text-gray-300 mt-4 text-lg max-w-2xl">
                  {user.bio}
                </p>
              )}

              {/* Meta Info */}
              <div className="flex flex-wrap gap-6 mt-6 text-gray-400">
                {user.location && (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5" />
                    <span>{user.location}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  <span>Joined {new Date(user.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                  <div className="flex items-center gap-2 text-yellow-400 mb-1">
                    <Briefcase className="w-5 h-5" />
                  </div>
                  <div className="text-2xl font-bold text-white">{stats.total_content}</div>
                  <div className="text-sm text-gray-400">Content</div>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                  <div className="flex items-center gap-2 text-blue-400 mb-1">
                    <Eye className="w-5 h-5" />
                  </div>
                  <div className="text-2xl font-bold text-white">{stats.total_views}</div>
                  <div className="text-sm text-gray-400">Views</div>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                  <div className="flex items-center gap-2 text-red-400 mb-1">
                    <Heart className="w-5 h-5" />
                  </div>
                  <div className="text-2xl font-bold text-white">{stats.total_likes}</div>
                  <div className="text-sm text-gray-400">Likes</div>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                  <div className="flex items-center gap-2 text-purple-400 mb-1">
                    <Share2 className="w-5 h-5" />
                  </div>
                  <div className="text-2xl font-bold text-white">{stats.followers}</div>
                  <div className="text-sm text-gray-400">Followers</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="container mx-auto px-6 py-12">
        <h2 className="text-3xl font-bold text-white mb-6">Content by {user.display_name || user.username}</h2>
        
        {content && content.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {content.map((item) => (
              <Card key={item.id} className="bg-gray-800/50 border-gray-700 hover:border-yellow-500/50 transition-all cursor-pointer hover:scale-105">
                <CardContent className="p-4">
                  {/* Thumbnail */}
                  {item.thumbnail_url && (
                    <img
                      src={item.thumbnail_url}
                      alt={item.title}
                      className="w-full h-48 object-cover rounded-lg mb-3"
                    />
                  )}
                  
                  {/* Title */}
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="text-lg font-semibold text-white line-clamp-2 flex-1">
                      {item.title}
                    </h3>
                    <div className="flex items-center gap-1 text-yellow-400">
                      {getContentIcon(item.content_type)}
                    </div>
                  </div>

                  {/* Description */}
                  {item.description && (
                    <p className="text-sm text-gray-400 line-clamp-2 mb-3">
                      {item.description}
                    </p>
                  )}

                  {/* Stats */}
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <div className="flex items-center gap-1">
                      <Eye className="w-3 h-3" />
                      <span>{item.view_count || 0} views</span>
                    </div>
                    <span>{new Date(item.created_at).toLocaleDateString()}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <Briefcase className="w-16 h-16 mx-auto mb-4 text-gray-600" />
            <p className="text-gray-400">No public content yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfilePage;